<template>
    <div>
        <div v-if="hasActivityContent" class="columns tw:pt-4">
            <div class="column">
                <div class="card discussion-thread-activity-content">
                    <header class="card-header">
                        <button
                            id="course-activity-discussion-instructions-toggle"
                            type="button"
                            class="card-header-title is-clickable tw:w-full tw:border-0 tw:bg-transparent tw:p-0 tw:text-left tw:font-inherit"
                            :aria-expanded="displayActivityContent"
                            aria-controls="course-activity-discussion-instructions-panel"
                            @click="toggleActivityContent"
                            @keydown.enter.prevent="toggleActivityContent"
                            @keydown.space.prevent="toggleActivityContent"
                        >
                            <div class="media">
                                <div class="media-left">
                                    <e-icon :icon="displayActivityContent ? 'angle-up' : 'angle-down'" />

                                    <span>{{ translate('activity_instructions') }}</span>
                                </div>
                            </div>
                        </button>
                    </header>

                    <div
                        v-show="displayActivityContent"
                        id="course-activity-discussion-instructions-panel"
                        class="card-content"
                        role="region"
                        aria-labelledby="course-activity-discussion-instructions-toggle"
                    >
                        <e-field>
                            <display-content :content="activity.activity_content.content" />
                        </e-field>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="activity && activity.rubric" class="columns">
            <div class="column">
                <display-rubric
                    :rubric="{
                        id: activity.rubric.id,
                        namespace: rubricNamespace,
                    }"
                />
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <section>
                    <div class="columns content is-vcentered">
                        <div class="column">
                            <h2>Discussions</h2>
                        </div>

                        <div v-if="!readOnly" class="column is-flex is-flex-end-desktop">
                            <e-button
                                type="is-primary"
                                :disabled="disabled"
                                @click="maxThreadCheck"
                                @keyup="maxThreadCheck"
                            >
                                {{ translate('start_new_discussion') }}
                            </e-button>
                        </div>
                    </div>

                    <div v-if="hasAnyAttemptAuthorizations" class="columns">
                        <div class="column">
                            <attempt-authorization-notice :attempt-authorizations="userUnusedAuthorizedAttempts" />
                        </div>
                    </div>

                    <div v-if="thread_limit_reached && !userHasAuthorizedAttempt" class="columns">
                        <div class="column">
                            <e-message type="is-info">
                                {{ translate('thread_limit_reached') }}
                            </e-message>
                        </div>
                    </div>

                    <div class="columns">
                        <div class="column">
                            <hr />
                        </div>
                    </div>

                    <div class="columns">
                        <div class="column discussion-thread-table-section">
                            <template v-if="discussions.length">
                                <div v-if="isMobileDiscussionLayout" class="discussion-thread-mobile-list">
                                    <article
                                        v-for="discussion in paginatedDiscussions"
                                        :key="discussion.id"
                                        class="discussion-thread-mobile-card"
                                    >
                                        <router-link
                                            class="discussion-thread-mobile-title"
                                            :to="viewThread(discussion.id)"
                                            :title="discussion.title"
                                        >
                                            {{ discussion.title }}
                                        </router-link>

                                        <div class="discussion-thread-mobile-meta">
                                            <span class="discussion-thread-mobile-label">Author</span>
                                            <user-menu :user="discussion.user">
                                                <template #user-name>
                                                    <div>{{ discussion.user.first_name }}</div>
                                                    <div>{{ discussion.user.last_name }}</div>
                                                </template>
                                            </user-menu>
                                        </div>

                                        <dl class="discussion-thread-mobile-stats">
                                            <div class="discussion-thread-mobile-stat">
                                                <dt>Replies</dt>
                                                <dd>{{ discussion.reply_count }}</dd>
                                            </div>
                                            <div class="discussion-thread-mobile-stat">
                                                <dt>Posted</dt>
                                                <dd :title="longDate(discussion.created_at, $timezone)">
                                                    {{ shortDate(discussion.created_at, $timezone) }}
                                                </dd>
                                            </div>
                                            <div class="discussion-thread-mobile-stat">
                                                <dt>Last Activity</dt>
                                                <dd
                                                    :title="
                                                        discussion.reply_count
                                                            ? longDate(discussion.last_reply, $timezone)
                                                            : longDate(discussion.created_at, $timezone)
                                                    "
                                                >
                                                    {{
                                                        discussion.reply_count
                                                            ? shortDate(discussion.last_reply, $timezone)
                                                            : shortDate(discussion.created_at, $timezone)
                                                    }}
                                                </dd>
                                            </div>
                                        </dl>
                                    </article>

                                    <e-pagination
                                        v-if="discussionTotalPages > 1"
                                        class="discussion-thread-mobile-pagination"
                                        :current="currentPage"
                                        :total="discussions.length"
                                        :per-page="discussionsPerPage"
                                        @change="handleDiscussionPageChange"
                                    />
                                </div>

                                <e-table
                                    v-else
                                    class="discussion-thread-table"
                                    :data="discussions"
                                    :current-page="currentPage"
                                    paginated
                                    :per-page="discussionsPerPage"
                                    striped
                                    hoverable
                                    @page-change="handleDiscussionPageChange"
                                >
                                    <e-table-column v-slot="{ row }" field="title" label="Title">
                                        <router-link :to="viewThread(row.id)" :title="row.title">
                                            {{ truncate(row.title, 6) }}
                                        </router-link>
                                    </e-table-column>

                                    <e-table-column v-slot="{ row }" class="always-break" field="author" label="Author">
                                        <user-menu :user="row.user">
                                            <template #user-name>
                                                <div>{{ row.user.first_name }}</div>
                                                <div>{{ row.user.last_name }}</div>
                                            </template>
                                        </user-menu>
                                    </e-table-column>

                                    <e-table-column v-slot="{ row }" field="replies" label="Replies">
                                        {{ row.reply_count }}
                                    </e-table-column>

                                    <e-table-column
                                        v-slot="{ row }"
                                        :visible="showField"
                                        field="created_at"
                                        label="Date Posted"
                                        sortable
                                    >
                                        <span :title="longDate(row.created_at, $timezone)">
                                            {{ shortDate(row.created_at, $timezone) }}
                                        </span>
                                    </e-table-column>

                                    <e-table-column
                                        v-slot="{ row }"
                                        :visible="showField"
                                        field="last_reply"
                                        label="Last Activity Date"
                                        sortable
                                    >
                                        <span v-if="row.reply_count" :title="longDate(row.last_reply, $timezone)">
                                            {{ shortDate(row.last_reply, $timezone) }}
                                        </span>
                                        <span v-else :title="longDate(row.created_at, $timezone)">
                                            {{ shortDate(row.created_at, $timezone) }}
                                        </span>
                                    </e-table-column>
                                </e-table>
                            </template>

                            <p v-if="loadingDiscussions" class="has-text-subtle">Loading discussions...</p>
                            <p v-else-if="!loadingDiscussions && !discussions.length" class="has-text-subtle">No discussions to display.</p>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import filter from 'lodash/filter.js';
import get from 'lodash/get.js';
import some from 'lodash/some.js';
import values from 'lodash/values.js';
import { useI18n } from 'vue-i18n';
import { mapState } from 'vuex';

import DisplayContent from '@/components/editor/DisplayContent.vue';
import DisplayRubric from '@/components/rubric/display-rubric/DisplayRubric.vue';
import UserMenu from '@/components/user-menu/UserMenu.vue';
import AttemptAuthorizationMixin from '@/mixins/AttemptAuthorization.mixin.js';
import IsLateMixin from '@/mixins/IsLate.mixin.js';
import Activity from '@/models/Activity.js';
import Course from '@/models/Course.js';
import { longDate, shortDate } from '@/store/utilities/Helpers.js';
import { isDiscussionWeekRestrictionActive } from '@/utilities/discussionWeekRestriction.js';

export default {
    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },
    name: 'DiscussionThread',

    components: {
        DisplayContent,
        DisplayRubric,
        UserMenu,
    },

    mixins: [IsLateMixin, AttemptAuthorizationMixin],

    data: () => ({
        loadingDiscussions: false,
        currentPage: 1,
        discussionsPerPage: 15,
        displayActivityContent: true,
    }),

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),

        activity() {
            return this.namespace.includes('emp')
                ? this.$store.getters[`${this.namespace}/get`](this.uuid)
                : Activity.query().withAll().find(this.$route.params.activityId);
        },

        hasActivityContent() {
            return !!this.activity?.activity_content?.content;
        },

        submissions() {
            return this.$gate.allows('viewOther', ['ActivitySubmission', this.course])
                ? this.allSubmissions
                : this.userSubmissions;
        },

        userSubmissions() {
            const submissions = values(this.$store.getters['ActivitySubmissions/all']());

            return filter(
                submissions,
                submission =>
                    get(submission, 'created_by') === this.$store.getters['Auth/userId'] &&
                    get(submission, 'activity_id') === this.activity.id
            );
        },

        allSubmissions() {
            const submissions = values(this.$store.getters['ActivitySubmissions/all']());

            return filter(submissions, submission => get(submission, 'activity_id') === this.activity.id);
        },

        thread_limit_reached() {
            const maxThreads = this.activity?.activity_content?.max_threads || 0;
            const threads = this.activity?.activity_content?.threads || 0;

            return maxThreads > 0 && maxThreads === threads;
        },

        discussions() {
            if (this.readOnly) {
                return [];
            }

            return values(this.$store.getters['DiscussionThreads/all']()).filter(
                discussion =>
                    Number(discussion.course_id) === Number(this.activity.course_id) &&
                    Number(discussion.activity_id) === Number(this.activity.id)
            );
        },

        course() {
            return Course.query().withAll().find(this.$route.params.courseId);
        },

        rubricNamespace() {
            return this.namespace.includes('Temp') ? 'TemplateRubrics' : 'Rubrics';
        },

        showField() {
            return this.windowWidth >= 1320;
        },

        isStudentOrTeachingAssistant() {
            const roles = this.$store.getters['ActiveEnrollment/roles'];
            if (!roles) return false;
            return roles.some(role => role.name === 'Student' || role.name === 'Teaching Assistant');
        },

        discussionRestrictedUntilWeekStart() {
            return (
                this.isStudentOrTeachingAssistant &&
                isDiscussionWeekRestrictionActive(this.activity?.activity_content)
            );
        },

        isMobileDiscussionLayout() {
            return this.$isMobile || this.$isTablet;
        },

        paginatedDiscussions() {
            const start = (this.currentPage - 1) * this.discussionsPerPage;
            return this.discussions.slice(start, start + this.discussionsPerPage);
        },

        discussionTotalPages() {
            return Math.ceil(this.discussions.length / this.discussionsPerPage);
        },

        disabled() {
            if (this.authUser.is_admin) {
                return false;
            }

            return some([
                this.usedAllAttemptAuthorizations,
                this.thread_limit_reached && !this.userHasAuthorizedAttempt,
                this.readOnly,
                this.$gate.denies('create', ['DiscussionThread', this.course]),
                this.isLate && !this.canSubmitLate && !this.userHasAuthorizedAttempt,
                this.discussionRestrictedUntilWeekStart,
            ]);
        },
    },

    async created() {
        if (this.activity && !this.readOnly && this.$gate.allows('create', ['DiscussionThread', this.course])) {
            this.verifyActivity();
        }

        // Load discussion threads for this activity
        if (this.activity && this.activity.activity_type === 'discussion-thread') {
            this.loadingDiscussions = true;
            try {
                await this.$store.dispatch('DiscussionThreads/fetch', {
                    courseId: this.$route.params.courseId,
                    activityId: this.$route.params.activityId,
                });
            } catch (error) {
                console.error('Failed to load discussion threads:', error);
            } finally {
                this.loadingDiscussions = false;
            }
        }
    },

    methods: {
        toggleActivityContent() {
            this.displayActivityContent = !this.displayActivityContent;
        },

        get(field) {
            return this.$store.getters[`${this.namespace}/${this.uuid}/get`](field);
        },

        viewThread(id) {
            return {
                name: 'discussion-thread-view',
                params: {
                    courseId: this.$route.params.courseId,
                    activityId: this.$route.params.activityId,
                    id,
                },
            };
        },

        verifyActivity() {
            return this.$store.dispatch('Activities/verifyActivity', {
                course_id: this.activity.course_id,
                activity_id: this.activity.id,
            });
        },

        maxThreadCheck() {
            if (this.thread_limit_reached && !this.userHasAuthorizedAttempt) return;
            if (this.discussionRestrictedUntilWeekStart) return;

            this.$router.push({
                name: 'discussion-thread-create',
                params: { activityId: this.activity.id },
            });
        },

        handleDiscussionPageChange(page) {
            this.currentPage = page;
        },

        longDate,
        shortDate,
    },
};
</script>

<style scoped lang="scss">
.discussion-thread-table-section {
    padding-bottom: 1rem;
    margin-bottom: 1rem;
}

.discussion-thread-mobile-list {
    display: grid;
    gap: 0.75rem;
}

.discussion-thread-mobile-card {
    border: 1px solid var(--p-content-border-color);
    border-radius: 0.75rem;
    background: var(--p-content-background);
    padding: 1rem;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}

.discussion-thread-mobile-title {
    display: block;
    font-weight: 700;
    line-height: 1.4;
    margin-bottom: 0.75rem;
}

.discussion-thread-mobile-meta {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.75rem;
}

.discussion-thread-mobile-label {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--p-text-muted-color);
}

.discussion-thread-mobile-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
    margin: 0;
}

.discussion-thread-mobile-stat {
    min-width: 0;
}

.discussion-thread-mobile-stat dt {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--p-text-muted-color);
    margin-bottom: 0.25rem;
}

.discussion-thread-mobile-stat dd {
    margin: 0;
    font-size: 0.9375rem;
    line-height: 1.4;
}

.discussion-thread-mobile-pagination {
    margin-top: 0.5rem;
}

:deep(.discussion-thread-table .p-datatable-paginator-bottom) {
    justify-content: flex-end;
}

:deep(.discussion-thread-table .p-datatable-paginator-bottom .p-paginator) {
    width: 100%;
    justify-content: flex-end;
}

:deep(.discussion-thread-table .p-datatable-paginator-bottom .primevue-pagination) {
    margin-left: auto;
    justify-content: flex-end;
}

@media screen and (max-width: 768px) {
    .discussion-thread-mobile-stats {
        grid-template-columns: 1fr;
    }

    :deep(.discussion-thread-table .p-datatable-paginator-bottom) {
        justify-content: flex-end;
    }

    :deep(.discussion-thread-table .p-datatable-paginator-bottom .p-paginator) {
        justify-content: flex-end;
    }

    :deep(.discussion-thread-table .p-datatable-paginator-bottom .primevue-pagination) {
        margin-left: auto;
        justify-content: flex-end;
    }
}
</style>
